/**
 * responseMinimalismGate.js — FASE R7.
 *
 * Forces shorter responses when the context already provides sufficient
 * emotional clarity, the user wrote little, or the session is deep.
 *
 * A human pastor naturally says less as they know someone better.
 * Responses should DECREASE in elaboration as conversation deepens.
 *
 * Output instruction: ≤50 chars. Suppresses artificial expansion.
 */

const LOGISTIC_INTENTS = new Set(["planning", "logistics", "analysis", "events"]);

/**
 * @param {{
 *   userLength:         number,   — char count of user message
 *   emotion:            string,
 *   intensity:          string,
 *   conversationLength: number,
 *   intent:             string,
 *   understateMode:     boolean,
 * }} params
 * @returns {{
 *   minimalismLevel:       "high"|"moderate"|"low",
 *   maxSuggestedSentences: number|null,
 *   instruction:           string|null,
 * }}
 */
export function resolveMinimalism({ userLength = 0, emotion = "neutral", intensity = "low", conversationLength = 0, intent = "general", understateMode = false }) {
  // Logistics → direct and minimal
  if (LOGISTIC_INTENTS.has(intent)) {
    return { minimalismLevel: "high", maxSuggestedSentences: 3, instruction: "Respuesta corta." };
  }

  // Critical / deep grief conversation → maximum restraint
  if ((emotion === "grief" || intensity === "critical") && conversationLength >= 4) {
    return { minimalismLevel: "high", maxSuggestedSentences: 3, instruction: "Menos cierre." };
  }

  // Grief (any depth)
  if (emotion === "grief" || emotion === "pastoral-care") {
    return { minimalismLevel: "high", maxSuggestedSentences: 4, instruction: "No llenes silencios." };
  }

  // Understatement mode active (from R6 / grief / exhaustion)
  if (understateMode) {
    return { minimalismLevel: "moderate", maxSuggestedSentences: 5, instruction: "No desarrolles demasiado." };
  }

  // Short user message → don't compensate with long response
  if (userLength > 0 && userLength < 40) {
    return { minimalismLevel: "moderate", maxSuggestedSentences: 4, instruction: "No expandas artificialmente." };
  }

  // Late in a long conversation → responses naturally decrease
  if (conversationLength >= 8) {
    return { minimalismLevel: "moderate", maxSuggestedSentences: 5, instruction: null };
  }

  return { minimalismLevel: "low", maxSuggestedSentences: null, instruction: null };
}
